import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, CircularProgress, Chip, IconButton, Tooltip, TextField, Card,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select,
  FormControl, InputLabel, InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';
import { formatDate as formatDateUtil, isOverdue as isOverdueUtil } from '../../../../utils/dateUtils';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
}));

const StyledSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover fieldset': { borderColor: '#10b981' },
    '&.Mui-focused fieldset': { borderColor: '#10b981' },
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '10px 20px',
  borderRadius: '10px',
  fontWeight: 600,
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
}));

const StatCard = styled(Card)(({ theme, color }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: '#fff',
  border: `1px solid ${color}20`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  '& .stat-value': { fontSize: '28px', fontWeight: 700, color: color },
  '& .stat-label': { fontSize: '13px', color: '#6b7280', marginTop: '4px' },
}));

const TableCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  '& .MuiTableCell-head': {
    fontWeight: 600,
    color: '#374151',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '16px',
    borderBottom: '2px solid #e5e7eb',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': { backgroundColor: '#f0fdf4' },
  '& .MuiTableCell-body': {
    padding: '16px',
    borderBottom: '1px solid #f3f4f6',
  },
}));

const StatusChip = styled(Chip)(({ status }) => {
  const colors = {
    pending: { bg: '#fef3c7', color: '#d97706' },
    in_progress: { bg: '#dbeafe', color: '#2563eb' },
    completed: { bg: '#d1fae5', color: '#059669' },
    cancelled: { bg: '#fee2e2', color: '#dc2626' },
    overdue: { bg: '#fecaca', color: '#b91c1c' },
  };
  const c = colors[status] || colors.pending;
  return {
    backgroundColor: c.bg,
    color: c.color,
    fontWeight: 600,
    fontSize: '12px',
  };
});

const PriorityChip = styled(Chip)(({ priority }) => {
  const colors = {
    high: { bg: '#fee2e2', color: '#dc2626' },
    normal: { bg: '#e0e7ff', color: '#4f46e5' },
    low: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const c = colors[priority] || colors.normal;
  return { backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: '11px' };
});

const FollowupListing = () => {
  const navigate = useNavigate();
  const { isBDM, isTelly } = useAdminRole();
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({ pending: 0, inProgress: 0, completed: 0, todayCount: 0, overdue: 0 });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, followup: null });
  const [completeDialog, setCompleteDialog] = useState({ open: false, followup: null });
  const [completeForm, setCompleteForm] = useState({ outcome: '', outcome_notes: '', call_duration: '' });

  const fetchFollowups = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Tab-based filtering
      if (activeTab === 1) params.append('status', 'pending');
      else if (activeTab === 2) params.append('today', 'true');
      else if (activeTab === 3) params.append('overdue', 'true');
      else if (activeTab === 4) params.append('status', 'completed');

      // Role-based filtering
      if (isTelly) {
        params.append('myFollowups', 'true');
      } else if (isBDM) {
        params.append('createdByMe', 'true');
      }

      const response = await ApiService.get(`/admin/followups?${params.toString()}`);
      const data = response?.data?.data || response?.data || response;
      setFollowups(data.followups || []);
    } catch (error) {
      logger.error('Error fetching followups:', error);
      setFollowups([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, isTelly, isBDM]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await ApiService.get('/admin/followups/stats/summary');
      const data = response?.data?.data || response?.data || response;
      setStats(data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchFollowups();
    fetchStats();
  }, [fetchFollowups, fetchStats]);

  const handleComplete = async () => {
    if (!completeDialog.followup) return;
    try {
      await ApiService.put(`/admin/followups/${completeDialog.followup.id}/complete`, completeForm);
      setCompleteDialog({ open: false, followup: null });
      setCompleteForm({ outcome: '', outcome_notes: '', call_duration: '' });
      fetchFollowups();
      fetchStats();
    } catch (error) {
      logger.error('Error completing followup:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.followup) return;
    try {
      await ApiService.delete(`/admin/followups/${deleteDialog.followup.id}`);
      setDeleteDialog({ open: false, followup: null });
      fetchFollowups();
      fetchStats();
    } catch (error) {
      logger.error('Error deleting followup:', error);
    }
  };

  const filteredFollowups = followups.filter((f) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      f.title?.toLowerCase().includes(q) ||
      f.lead?.business_name?.toLowerCase().includes(q) ||
      f.lead?.contact_person?.toLowerCase().includes(q) ||
      f.assignedEmployee?.first_name?.toLowerCase().includes(q)
    );
  });

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateUtil(date);
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed' || status === 'cancelled') return false;
    return isOverdueUtil(dueDate);
  };

  return (
    <Container>
      <Header>
        <TitleSection>
          <TitleIcon>
            <AssignmentIcon sx={{ fontSize: 24 }} />
          </TitleIcon>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#1f2937">Follow-ups</Typography>
            <Typography variant="body2" color="text.secondary">
              {isTelly ? 'Manage your assigned follow-ups' : 'Track follow-ups assigned to your team'}
            </Typography>
          </Box>
        </TitleSection>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <StyledSearchField
            placeholder="Search follow-ups..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9ca3af' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />
          <IconButton onClick={() => { fetchFollowups(); fetchStats(); }} sx={{ bgcolor: '#fff', boxShadow: 1 }}>
            <RefreshIcon />
          </IconButton>
          {isBDM && (
            <AddButton variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin-dashboard/add-followup')}>
              Add Follow-up
            </AddButton>
          )}
        </Box>
      </Header>


      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        <Tab label="All" />
        <Tab label="Pending" />
        <Tab label="Today" />
        <Tab label="Overdue" />
        <Tab label="Completed" />
      </Tabs>

      <TableCard>
        <TableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Lead/Client</TableCell>
                <TableCell>BDM</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress sx={{ color: '#10b981' }} />
                    <Typography sx={{ mt: 2 }} color="text.secondary">Loading follow-ups...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredFollowups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <AssignmentIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                    <Typography color="text.secondary">No follow-ups found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFollowups.map((followup) => {
                  const overdue = isOverdue(followup.due_date, followup.status);
                  return (
                    <StyledTableRow key={followup.id}>
                      <TableCell>
                        <Box>
                          <Typography fontWeight={600} color="#1f2937">{followup.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{followup.followup_type}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                          <Box>
                            <Typography fontWeight={500}>{followup.lead?.business_name || 'N/A'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {followup.lead?.contact_person}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary">{followup.lead?.bdm_name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {followup.assignedEmployee ? `${followup.assignedEmployee.first_name} ${followup.assignedEmployee.last_name || ''}` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(followup.scheduled_date)}</TableCell>
                      <TableCell>
                        <Typography color={overdue ? '#dc2626' : 'inherit'} fontWeight={overdue ? 600 : 400}>
                          {formatDate(followup.due_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <PriorityChip priority={followup.priority} label={followup.priority || 'normal'} size="small" />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={overdue ? 'overdue' : followup.status} label={overdue ? 'Overdue' : followup.status} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => navigate(`/admin-dashboard/followup/${followup.id}`)}>
                              <VisibilityIcon fontSize="small" sx={{ color: '#6b7280' }} />
                            </IconButton>
                          </Tooltip>
                          {followup.status !== 'completed' && (
                            <Tooltip title="Mark Complete">
                              <IconButton size="small" onClick={() => setCompleteDialog({ open: true, followup })} sx={{ color: '#10b981' }}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {followup.lead?.phone_number && (
                            <Tooltip title="Call">
                              <IconButton size="small" component="a" href={`tel:${followup.lead.phone_number}`} sx={{ color: '#3b82f6' }}>
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isBDM && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => setDeleteDialog({ open: true, followup })} sx={{ color: '#ef4444' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      {/* Complete Dialog */}
      <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, followup: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Complete Follow-up</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Outcome</InputLabel>
              <Select
                value={completeForm.outcome}
                onChange={(e) => setCompleteForm({ ...completeForm, outcome: e.target.value })}
                label="Outcome"
              >
                <MenuItem value="connected">Connected - Successful</MenuItem>
                <MenuItem value="no_answer">No Answer</MenuItem>
                <MenuItem value="busy">Busy - Call Back Later</MenuItem>
                <MenuItem value="wrong_number">Wrong Number</MenuItem>
                <MenuItem value="not_interested">Not Interested</MenuItem>
                <MenuItem value="follow_up_required">Follow-up Required</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Call Duration (minutes)"
              type="number"
              value={completeForm.call_duration}
              onChange={(e) => setCompleteForm({ ...completeForm, call_duration: e.target.value })}
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={completeForm.outcome_notes}
              onChange={(e) => setCompleteForm({ ...completeForm, outcome_notes: e.target.value })}
              placeholder="Add any notes about this follow-up..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCompleteDialog({ open: false, followup: null })}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleComplete}>Mark Complete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, followup: null })}>
        <DialogTitle>Delete Follow-up?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{deleteDialog.followup?.title}"? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, followup: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FollowupListing;
